import React, { useState } from 'react';
import axios from 'axios';
import { Play, Download, Loader2, TrendingUp, TrendingDown, Target, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import dayjs from 'dayjs';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SYMBOLS = [
  { symbol: 'BTCUSDT', name: 'Bitcoin' },
  { symbol: 'ETHUSDT', name: 'Ethereum' },
  { symbol: 'SOLUSDT', name: 'Solana' },
  { symbol: 'BNBUSDT', name: 'BNB' }
];

const STRATEGIES = [
  { id: 'RFS', name: 'Ruptura Fractal Sincronizada', description: 'Breakout strategy with consolidation detection' },
  { id: 'TRS', name: 'Resorte Mecánico (Trend Spring)', description: 'EMA pullback with stochastic confirmation' },
  { id: 'ARR', name: 'Asian Range Reversal', description: 'Failed breakout reversal on Asian session range (00-08 UTC)' },
  { id: 'CLS', name: 'Claude Strategy (Adaptive)', description: 'Multi-regime: trending (EMA+MACD) / ranging (Bollinger+Stoch) via ADX' }
];

export default function Backtesting() {
  const [params, setParams] = useState({
    symbol: 'BTCUSDT',
    interval: '15m',
    strategy: 'RFS',
    days: 90,
    initial_equity: 10000,
    base_risk_pct: 0.01,
    use_break_even: true,
    tp_ratio: 2.0,
    lookback: 10,
    vol_multiplier: 1.5,
    market_type: 'spot',
    leverage: 1,
    use_hmm_filter: false
  });
  
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runBacktest = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/backtest/run`, params);
      setResults(response.data);
      toast.success('Backtest completed successfully!');
    } catch (error) {
      console.error('Backtest error:', error);
      toast.error('Error running backtest');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!results?.trades?.length) {
      toast.error('No trades to export');
      return;
    }

    const headers = ['Entry Time', 'Exit Time', 'Type', 'Leverage', 'Entry Price', 'Exit Price', 'SL', 'TP', 'Size', 'PnL', 'PnL %', 'R Multiple', 'Exit Reason'];
    const rows = results.trades.map(t => [
      dayjs.unix(t.entry_time).format('YYYY-MM-DD HH:mm'),
      dayjs.unix(t.exit_time).format('YYYY-MM-DD HH:mm'),
      t.type,
      t.leverage || 1,
      t.entry_price,
      t.exit_price,
      t.stop_loss,
      t.take_profit,
      t.size?.toFixed(4),
      t.pnl,
      t.pnl_pct,
      t.r_multiple,
      t.exit_reason
    ]);

    const marketLabel = params.market_type === 'futures' ? `futures_${params.leverage}x` : 'spot';
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backtest_${params.strategy}_${params.symbol}_${marketLabel}_${Date.now()}.csv`;
    a.click();
    toast.success('CSV exported successfully!');
  };

  const updateParam = (key, value) => {
    setParams(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div>
      <header className="page-header">
        <div className="page-header-content">
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Backtesting</h1>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {results?.market_type === 'futures' && (
              <span className="badge" style={{ backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#F59E0B' }}>
                ⚡ Futures {results.leverage}x
              </span>
            )}
            {results?.simulated && (
              <span className="badge" style={{ backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#F59E0B' }}>
                Using Simulated Data
              </span>
            )}
          </div>
        </div>
      </header>

      <div className="page-container">
        <div className="backtest-grid">
          {/* Controls Panel */}
          <div className="backtest-controls">
            {/* Strategy Selection */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Strategy</h3>
              </div>
              <div className="card-content">
                <div className="form-group">
                  <select
                    value={params.strategy}
                    onChange={(e) => updateParam('strategy', e.target.value)}
                    className="form-select"
                    data-testid="strategy-select"
                  >
                    {STRATEGIES.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <p style={{ fontSize: '0.75rem', color: '#9AA3AF', marginTop: '0.5rem' }}>
                    {STRATEGIES.find(s => s.id === params.strategy)?.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Symbol & Timeframe */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Market</h3>
              </div>
              <div className="card-content">
                {/* Market Type Selector */}
                <div className="form-group">
                  <label className="form-label">Market Type</label>
                  <div className="market-type-toggle" style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => {
                        updateParam('market_type', 'spot');
                        updateParam('leverage', 1);
                      }}
                      className={`btn ${params.market_type === 'spot' ? 'btn-primary' : 'btn-outline'}`}
                      style={{ flex: 1 }}
                      data-testid="backtest-market-spot"
                    >
                      Spot
                    </button>
                    <button
                      type="button"
                      onClick={() => updateParam('market_type', 'futures')}
                      className={`btn ${params.market_type === 'futures' ? 'btn-primary' : 'btn-outline'}`}
                      style={{ flex: 1, backgroundColor: params.market_type === 'futures' ? '#F59E0B' : undefined, borderColor: params.market_type === 'futures' ? '#F59E0B' : undefined }}
                      data-testid="backtest-market-futures"
                    >
                      ⚡ Futures
                    </button>
                  </div>
                </div>

                {/* Leverage Selector (only for Futures) */}
                {params.market_type === 'futures' && (
                  <div className="form-group">
                    <label className="form-label">Leverage</label>
                    <select
                      value={params.leverage}
                      onChange={(e) => updateParam('leverage', parseInt(e.target.value))}
                      className="form-select"
                      style={{ borderColor: '#F59E0B' }}
                      data-testid="backtest-leverage-select"
                    >
                      {[1, 2, 3, 5, 10].map(lev => (
                        <option key={lev} value={lev}>{lev}x</option>
                      ))}
                    </select>
                    <p style={{ fontSize: '0.7rem', color: '#F59E0B', marginTop: '0.25rem' }}>
                      ⚠️ Mayor apalancamiento = Mayor riesgo de liquidación
                    </p>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Symbol</label>
                  <select
                    value={params.symbol}
                    onChange={(e) => updateParam('symbol', e.target.value)}
                    className="form-select"
                    data-testid="backtest-symbol-select"
                  >
                    {SYMBOLS.map(s => (
                      <option key={s.symbol} value={s.symbol}>{s.name} ({s.symbol})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Timeframe</label>
                  <select
                    value={params.interval}
                    onChange={(e) => updateParam('interval', e.target.value)}
                    className="form-select"
                  >
                    <option value="5m">5 minutes</option>
                    <option value="15m">15 minutes</option>
                    <option value="1h">1 hour</option>
                    <option value="4h">4 hours</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">History (Days)</label>
                  <input
                    type="number"
                    value={params.days}
                    onChange={(e) => updateParam('days', parseInt(e.target.value))}
                    className="form-input"
                    min="30"
                    max="365"
                  />
                </div>
              </div>
            </div>

            {/* Risk Management */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Risk Management</h3>
              </div>
              <div className="card-content">
                <div className="form-group">
                  <label className="form-label">Initial Equity ($)</label>
                  <input
                    type="number"
                    value={params.initial_equity}
                    onChange={(e) => updateParam('initial_equity', parseFloat(e.target.value))}
                    className="form-input"
                    data-testid="param-initial-equity"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Base Risk (%)</label>
                  <input
                    type="number"
                    value={params.base_risk_pct * 100}
                    onChange={(e) => updateParam('base_risk_pct', parseFloat(e.target.value) / 100)}
                    className="form-input"
                    step="0.1"
                    min="0.1"
                    max="5"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Take Profit Ratio</label>
                  <input
                    type="number"
                    value={params.tp_ratio}
                    onChange={(e) => updateParam('tp_ratio', parseFloat(e.target.value))}
                    className="form-input"
                    data-testid="param-tp-ratio"
                    step="0.5"
                    min="1"
                    max="5"
                  />
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={params.use_break_even}
                    onChange={(e) => updateParam('use_break_even', e.target.checked)}
                    id="break-even"
                    style={{ width: '18px', height: '18px' }}
                  />
                  <label htmlFor="break-even" style={{ fontSize: '0.875rem', color: '#E6E8EC' }}>
                    Move to Break-Even at 1R
                  </label>
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={params.use_hmm_filter}
                    onChange={(e) => updateParam('use_hmm_filter', e.target.checked)}
                    id="hmm-filter"
                    style={{ width: '18px', height: '18px', accentColor: '#8B5CF6' }}
                  />
                  <label htmlFor="hmm-filter" style={{ fontSize: '0.875rem', color: '#E6E8EC' }}>
                    🧠 HMM Regime Filter (Markov)
                  </label>
                </div>
                <p style={{ fontSize: '0.7rem', color: '#8B5CF6', marginTop: '0.25rem', marginLeft: '2rem' }}>
                  Filters signals through market regime detection (bull/bear/choppy)
                </p>
              </div>
            </div>

            {/* Strategy Params */}
            {params.strategy === 'RFS' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">RFS Parameters</h3>
                </div>
                <div className="card-content">
                  <div className="form-group">
                    <label className="form-label">Consolidation Lookback</label>
                    <input
                      type="number"
                      value={params.lookback}
                      onChange={(e) => updateParam('lookback', parseInt(e.target.value))}
                      className="form-input"
                      data-testid="param-lookback"
                      min="5"
                      max="30"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Volume Multiplier</label>
                    <input
                      type="number"
                      value={params.vol_multiplier}
                      onChange={(e) => updateParam('vol_multiplier', parseFloat(e.target.value))}
                      className="form-input"
                      step="0.1"
                      min="1"
                      max="5"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Run Button */}
            <button
              className="btn btn-primary btn-lg"
              onClick={runBacktest}
              disabled={loading}
              style={{ width: '100%' }}
              data-testid="backtest-run-button"
            >
              {loading ? (
                <><Loader2 size={20} className="loading-spinner" /> Running...</>
              ) : (
                <><Play size={20} /> Run Backtest</>
              )}
            </button>
          </div>

          {/* Results Panel */}
          <div className="backtest-results">
            {!results && !loading && (
              <div className="card">
                <div className="card-content empty-state">
                  <Target size={48} style={{ color: '#4B5563', marginBottom: '1rem' }} />
                  <p>Configure parameters and run a backtest to see results</p>
                </div>
              </div>
            )}

            {results && (
              <>
                {/* Metrics Cards */}
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-label">Total PnL</div>
                    <div className={`stat-value font-mono ${results.total_pnl >= 0 ? 'positive' : 'negative'}`}>
                      ${results.total_pnl?.toLocaleString()}
                    </div>
                    <div className={`stat-change ${results.return_pct >= 0 ? 'positive' : 'negative'}`}>
                      {results.return_pct >= 0 ? '+' : ''}{results.return_pct}%
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Win Rate</div>
                    <div className={`stat-value font-mono ${results.win_rate >= 50 ? 'positive' : 'negative'}`}>
                      {results.win_rate}%
                    </div>
                    <div className="stat-change">
                      {results.winning_trades}W / {results.losing_trades}L
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Profit Factor</div>
                    <div className={`stat-value font-mono ${results.profit_factor >= 1.5 ? 'positive' : results.profit_factor < 1 ? 'negative' : ''}`}>
                      {results.profit_factor}
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Max Drawdown</div>
                    <div className="stat-value font-mono negative">
                      {results.max_drawdown}%
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Avg R-Multiple</div>
                    <div className={`stat-value font-mono ${results.avg_r_multiple >= 0 ? 'positive' : 'negative'}`}>
                      {results.avg_r_multiple}R
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Total Trades</div>
                    <div className="stat-value font-mono">
                      {results.total_trades}
                    </div>
                  </div>
                </div>

                {/* Trades Table */}
                <div className="card">
                  <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 className="card-title">Trade History</h3>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={exportCSV}
                      data-testid="export-csv-button"
                    >
                      <Download size={16} /> Export CSV
                    </button>
                  </div>
                  <div style={{ maxHeight: '500px', overflow: 'auto' }}>
                    <table className="data-table" data-testid="trades-table">
                      <thead>
                        <tr>
                          <th>Entry</th>
                          <th>Exit</th>
                          <th>Type</th>
                          <th>Entry $</th>
                          <th>Exit $</th>
                          <th>PnL</th>
                          <th>R</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.trades?.map((trade, i) => (
                          <tr key={i}>
                            <td className="mono" style={{ fontSize: '0.75rem' }}>
                              {dayjs.unix(trade.entry_time).format('MM/DD HH:mm')}
                            </td>
                            <td className="mono" style={{ fontSize: '0.75rem' }}>
                              {dayjs.unix(trade.exit_time).format('MM/DD HH:mm')}
                            </td>
                            <td>
                              <span className={`badge ${trade.type?.toLowerCase()}`}>
                                {trade.type === 'LONG' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                {trade.type}
                              </span>
                            </td>
                            <td className="mono">${trade.entry_price?.toFixed(2)}</td>
                            <td className="mono">${trade.exit_price?.toFixed(2)}</td>
                            <td className={`mono ${trade.pnl >= 0 ? 'positive' : 'negative'}`} style={{ color: trade.pnl >= 0 ? '#22C55E' : '#EF4444' }}>
                              {trade.pnl >= 0 ? '+' : ''}${trade.pnl?.toFixed(2)}
                            </td>
                            <td className={`mono ${trade.r_multiple >= 0 ? '' : ''}`} style={{ color: trade.r_multiple >= 0 ? '#22C55E' : '#EF4444' }}>
                              {trade.r_multiple >= 0 ? '+' : ''}{trade.r_multiple}R
                            </td>
                            <td style={{ fontSize: '0.75rem' }}>
                              <span className={`badge ${trade.exit_reason === 'Take Profit' ? 'win' : 'loss'}`}>
                                {trade.exit_reason}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
